import React, { useState, useRef, useEffect } from 'react';
import { RefreshCw } from 'lucide-react';

const PullToRefresh = ({ onRefresh, children }) => {
  const [isPulling, setIsPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const startY = useRef(0);
  const containerRef = useRef(null);
  
  const threshold = 80; // Distance needed to trigger refresh

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleTouchStart = (e) => {
      // Only activate if at the top of the page
      if (window.scrollY === 0) {
        startY.current = e.touches[0].clientY;
      }
    };

    const handleTouchMove = (e) => {
      if (startY.current === 0 || window.scrollY > 0) return;

      const currentY = e.touches[0].clientY;
      const distance = currentY - startY.current;

      if (distance > 0 && distance < threshold * 2) {
        setIsPulling(true);
        setPullDistance(distance);
        
        // Prevent default pull-to-refresh on some mobile browsers
        if (distance > 10) {
          e.preventDefault();
        }
      }
    };

    const handleTouchEnd = async () => {
      if (pullDistance > threshold && !isRefreshing) {
        setIsRefreshing(true);
        try {
          await onRefresh();
        } catch (error) {
          console.error('Refresh failed:', error);
        } finally {
          setTimeout(() => {
            setIsRefreshing(false);
            setIsPulling(false);
            setPullDistance(0);
          }, 500);
        }
      } else {
        setIsPulling(false);
        setPullDistance(0);
      }
      startY.current = 0;
    };

    container.addEventListener('touchstart', handleTouchStart);
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [pullDistance, isRefreshing, onRefresh, threshold]);

  const pullPercentage = Math.min((pullDistance / threshold) * 100, 100);
  const showRefreshIndicator = isPulling || isRefreshing;

  return (
    <div ref={containerRef} className="relative min-h-screen">
      {/* Pull to Refresh Indicator */}
      {showRefreshIndicator && (
        <div 
          className="fixed top-0 left-0 right-0 z-50 flex justify-center pointer-events-none"
          style={{
            transform: `translateY(${Math.min(pullDistance, threshold)}px)`,
            transition: isRefreshing ? 'transform 0.3s ease-out' : 'none'
          }}
        >
          <div className="bg-gradient-to-r from-teal-500 to-cyan-600 text-white px-6 py-3 rounded-full shadow-lg flex items-center gap-2">
            <RefreshCw 
              className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`}
              style={{
                transform: `rotate(${pullPercentage * 3.6}deg)`,
                transition: isRefreshing ? 'none' : 'transform 0.1s'
              }}
            />
            <span className="font-semibold text-sm">
              {isRefreshing ? 'Refreshing...' : pullDistance > threshold ? 'Release to refresh' : 'Pull to refresh'}
            </span>
          </div>
        </div>
      )}
      
      {/* Content - Only transform when actively pulling, not blocking scroll */}
      <div 
        className="w-full"
        style={{ 
          transform: showRefreshIndicator ? `translateY(${Math.min(pullDistance * 0.3, threshold * 0.3)}px)` : 'none',
          transition: isRefreshing || !isPulling ? 'transform 0.3s ease-out' : 'none'
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default PullToRefresh;
